import { useCallback, useEffect, useRef, useState } from "react";
import getClient from "../helpers/get-client";

// Cobro en Chile (Flow, CLP) y fuera (Paddle, USD): contrato de
// `.claude/specs/1680-cobro-internacional/design.md` § "Firmas / contratos nuevos".
//
//   GET  /api/billing/me/checkout/?pais=XX   -> riel, moneda, planes, paddle{...}
//   POST /api/billing/me/checkout/           -> {riel:"flow", url_registro, plan, precio, moneda}
//                                              | {riel:"paddle", price_id, email, custom_data:{user_id}}
//   POST ... {metodo:"transferencia"}        -> {riel:"transferencia", plan, monto_usd, moneda, datos, nota}   (R42)
//   Rechazos: {detail, code} con code = plan_invalido | terminos_requeridos | pais_sin_cobro |
//   riel_no_habilitado | transferencia_no_disponible | ya_suscrito | precio_no_calza | flow_error.
//   El GET puede traer `transferencia: {datos:[{etiqueta,valor}], nota, montos:{plan: usd}}` (R40).
//   La forma EXACTA de cada respuesta (GET y POST) es el snapshot del backend
//   `app/billing/tests/fixtures/checkout_contrato.json`, copiado a `e2e/_helpers/checkout-contrato.json`
//   (revisor r3 F-1: este comentario decía `{metodo, datos, nota, monto}`, que no existe).
//
// El GET responde aunque el acceso esté bloqueado (la pantalla de bloqueo es
// justo donde se paga), así que este hook sirve en el gate. Cubre R26, R27, R31.

export const INTERVALO_SONDEO_MS = 3000;
export const PLAZO_SONDEO_MS = 60000;

// "Listo" = la suscripción quedó activa Y el acceso está permitido. Un pago
// confirmado en Paddle o Flow llega al backend por webhook, no por este front:
// la única fuente de verdad es `/api/user/me/`.
export const accesoLiberado = (usuario) =>
  usuario?.suscripcion?.status === "activa" && usuario?.access?.permite === true;

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Consulta `refreshUser` cada `intervalo` hasta `plazo`. Devuelve "listo" o
// "pendiente". La primera consulta es inmediata (el webhook suele llegar antes
// de que el médico vuelva). Un error de red no corta el sondeo: se reintenta.
// `cancelado()` deja salir si el componente se desmontó.
export const sondearAcceso = async (
  refreshUser,
  { intervalo = INTERVALO_SONDEO_MS, plazo = PLAZO_SONDEO_MS, cancelado = () => false } = {}
) => {
  const inicio = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const usuario = await refreshUser();
      if (accesoLiberado(usuario)) return "listo";
    } catch {
      /* red caída un momento: se sigue intentando hasta cumplir el plazo */
    }
    if (cancelado()) return "cancelado";
    if (Date.now() - inicio + intervalo > plazo) return "pendiente";
    await esperar(intervalo);
    if (cancelado()) return "cancelado";
  }
};

const codigoDeError = (err) => err?.response?.data?.code || null;

export default function useCheckout() {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const vivo = useRef(true);
  const secuencia = useRef(0);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  // `pais` vacío = el backend usa el país del perfil. Respuestas viejas (el
  // médico cambió de país dos veces seguidas) no pisan a la última (R27).
  const cargar = useCallback(async (pais) => {
    const mia = ++secuencia.current;
    setCargando(true);
    setError(false);
    try {
      const res = await getClient().get("/api/billing/me/checkout/", {
        params: pais ? { pais } : undefined,
      });
      if (vivo.current && mia === secuencia.current) setEstado(res.data);
    } catch {
      if (vivo.current && mia === secuencia.current) setError(true);
    } finally {
      if (vivo.current && mia === secuencia.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Devuelve `{ok:true, data}` o `{ok:false, code}`: el que llama decide el copy.
  const iniciar = useCallback(async ({ plan, pais, aceptaTerminos, metodo, ordenHash }) => {
    try {
      const body = { plan, pais };
      if (aceptaTerminos) body.acepta_terminos = true;
      // #1809: la orden de contratación aceptada (Flow). El back recalcula el texto y exige este hash.
      if (ordenHash) body.orden_hash = ordenHash;
      // R42: "transferencia" = pago manual (suscripción pendiente hasta que el admin lo registre).
      if (metodo) body.metodo = metodo;
      const res = await getClient().post("/api/billing/me/checkout/", body);
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, code: codigoDeError(err), status: err?.response?.status || null };
    }
  }, []);

  return { estado, cargando, error, cargar, iniciar };
}
